import { useState, useEffect } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import useBranchStore from "@/store/branchStore";
import { 
  Factory, Package, AlertTriangle, CheckCircle, Loader2, 
  Search, Filter, Calendar, FileText, ChevronDown, ChevronUp,
  RefreshCw, Download, Plus, Eye, ArrowRight, Clock, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { resolveComponentDisplay } from "@/lib/mbCandidateDisplay";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RMProduction = () => {
  const { token, user } = useAuthStore();
  const { selectedBranch: globalBranch } = useBranchStore();
  
  // State
  const [activeTab, setActiveTab] = useState("produce");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);          // every active branch (names)
  const [allowedBranches, setAllowedBranches] = useState([]); // branches the user can target
  const [selectedBranch, setSelectedBranch] = useState(globalBranch || "");
  const [isAllBranchAccess, setIsAllBranchAccess] = useState(false);
  
  // Produce RM state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [manufacturableRMs, setManufacturableRMs] = useState([]);
  const [unconfiguredCategories, setUnconfiguredCategories] = useState([]);
  const [selectedRM, setSelectedRM] = useState(null);
  const [rmSearch, setRmSearch] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [productionDate, setProductionDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // REGRIND_RECYCLE_PRD §FR-C4 — per-batch regrind feeds. `regrindFeeds` is
  // an array of { rm_id, pct }. `regrindOptions` is filled lazily once the
  // user opens the panel — we filter to regrinds whose parent_grades match
  // the target POLY for this BOM. Empty by default → no-regrind path stays
  // byte-identical with pre-feature behaviour.
  const [regrindFeeds, setRegrindFeeds] = useState([]);
  const [regrindOptions, setRegrindOptions] = useState([]);
  const [targetPolyRmId, setTargetPolyRmId] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // ── Colourant-aware MB substitution (Jun 2026, P0) ────────────
  // Map from BOM's stored component_rm_id → operator-picked MB rm_id.
  // Seeded from `resolved_rm_id` on preview response so the confirm
  // path deducts from the same MB the user saw. Empty when the BOM
  // has no MB/PWD components with a colourant (regular POLY / SP
  // path stays byte-identical).
  const [substitutionOverrides, setSubstitutionOverrides] = useState({});
  
  // Production log state
  const [productionLog, setProductionLog] = useState([]);
  const [logFilters, setLogFilters] = useState({
    category: "",
    rm_id: "",
    start_date: "",
    end_date: ""
  });
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logTotalPages, setLogTotalPages] = useState(0);
  
  // Summary state
  const [summary, setSummary] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Consumption report state
  const [consumptionReport, setConsumptionReport] = useState(null);
  
  // Expanded log rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  const getHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  // Sync with global branch
  useEffect(() => {
    if (globalBranch && globalBranch !== selectedBranch) {
      setSelectedBranch(globalBranch);
    }
  }, [globalBranch]);

  // Fetch branches on mount
  useEffect(() => {
    fetchBranches();
  }, [token]);

  // Fetch data when branch changes
  useEffect(() => {
    if (selectedBranch) {
      fetchActiveCategories();
      if (activeTab === "log") fetchProductionLog();
      if (activeTab === "reports") {
        fetchSummary();
        fetchConsumptionReport();
      }
    }
  }, [selectedBranch]);

  // Fetch manufacturable RMs when branch or category changes
  useEffect(() => {
    if (selectedBranch) {
      fetchManufacturableRMs();
    }
  }, [selectedBranch, selectedCategory]);

  // Fetch log when filters change
  useEffect(() => {
    if (activeTab === "log" && selectedBranch) {
      fetchProductionLog();
    }
  }, [logFilters, logPage, activeTab]);

  // Fetch reports when period changes
  useEffect(() => {
    if (activeTab === "reports" && selectedBranch) {
      fetchSummary();
      fetchConsumptionReport();
    }
  }, [summaryPeriod, activeTab]);

  const fetchBranches = async () => {
    try {
      // Fetch two things in parallel:
      //  1. The full list of active branches (for the dropdown — every
      //     branch is shown so the user always sees the full org).
      //  2. The user's allowed branches (`/api/branch-ops/my-branches`) so
      //     we can lock the rows they can't target.
      // Returned values are branch *names* — `branch_rm_inventory.branch`
      // uses names, so passing names through end-to-end avoids the legacy
      // code/name mismatch that silently returned 0 stock.
      const [allRes, mineRes] = await Promise.all([
        axios.get(`${API}/branches/names`, { headers: getHeaders() }),
        axios.get(`${API}/branch-ops/my-branches`, { headers: getHeaders() }),
      ]);
      const all = allRes.data?.branches || [];
      const mine = mineRes.data?.branches || [];
      setBranches(all);
      setAllowedBranches(mine);
      setIsAllBranchAccess(Boolean(mineRes.data?.is_all_access));
      // Auto-select if no branch yet, or replace a stale persisted branch
      // the user no longer has access to.
      const allowedSet = new Set(mine);
      if (!selectedBranch && mine.length > 0) {
        setSelectedBranch(mine[0]);
      } else if (selectedBranch && !allowedSet.has(selectedBranch)) {
        setSelectedBranch(mine[0] || "");
      }
    } catch (err) {
      console.error("Failed to fetch branches:", err);
    }
  };

  const fetchActiveCategories = async () => {
    if (!selectedBranch) return;
    try {
      const res = await axios.get(`${API}/rm-production/active-categories?branch=${selectedBranch}`, { 
        headers: getHeaders() 
      });
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
    }
  };

  const fetchManufacturableRMs = async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      let url = `${API}/rm-production/manufacturable-rms?branch=${selectedBranch}`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      const res = await axios.get(url, { headers: getHeaders() });
      // Endpoint now returns { items, unconfigured_categories } — keep
      // backwards-compat by also accepting a raw array.
      const data = res.data || {};
      const items = Array.isArray(data) ? data : (data.items || []);
      setManufacturableRMs(items);
      setUnconfiguredCategories(Array.isArray(data) ? [] : (data.unconfigured_categories || []));
    } catch (err) {
      console.error("Failed to fetch manufacturable RMs:", err);
      setManufacturableRMs([]);
      setUnconfiguredCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductionLog = async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      let url = `${API}/rm-production/log?branch=${selectedBranch}&page=${logPage}&page_size=20`;
      if (logFilters.category) url += `&category=${logFilters.category}`;
      if (logFilters.rm_id) url += `&rm_id=${logFilters.rm_id}`;
      if (logFilters.start_date) url += `&start_date=${logFilters.start_date}`;
      if (logFilters.end_date) url += `&end_date=${logFilters.end_date}`;
      
      const res = await axios.get(url, { headers: getHeaders() });
      setProductionLog(res.data.items || []);
      setLogTotal(res.data.total || 0);
      setLogTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error("Failed to fetch production log:", err);
      setProductionLog([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!selectedBranch || !summaryPeriod.start || !summaryPeriod.end) return;
    try {
      const res = await axios.get(
        `${API}/rm-production/summary?branch=${selectedBranch}&start_date=${summaryPeriod.start}&end_date=${summaryPeriod.end}`,
        { headers: getHeaders() }
      );
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
      setSummary(null);
    }
  };

  const fetchConsumptionReport = async () => {
    if (!selectedBranch || !summaryPeriod.start || !summaryPeriod.end) return;
    try {
      const res = await axios.get(
        `${API}/rm-production/consumption-report?branch=${selectedBranch}&start_date=${summaryPeriod.start}&end_date=${summaryPeriod.end}`,
        { headers: getHeaders() }
      );
      setConsumptionReport(res.data);
    } catch (err) {
      console.error("Failed to fetch consumption report:", err);
      setConsumptionReport(null);
    }
  };

  const handlePreview = async () => {
    if (!selectedRM || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please select an RM and enter a valid quantity");
      return;
    }
    
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API}/rm-production/preview`,
        {
          branch: selectedBranch,
          rm_id: selectedRM.rm_id,
          quantity_to_produce: parseFloat(quantity),
          // REGRIND_RECYCLE_PRD §FR-C4 — pass current feeds + target POLY.
          regrind_feeds: regrindFeeds.length
            ? regrindFeeds.map(f => ({ rm_id: f.rm_id, pct: parseFloat(f.pct) || 0 }))
                          .filter(f => f.rm_id && f.pct > 0)
            : [],
          target_poly_rm_id: targetPolyRmId || null,
        },
        { headers: getHeaders() }
      );
      setPreviewData(res.data);
      setShowPreviewDialog(true);

      // Detect POLY components in the BOM and auto-fetch matching regrind
      // candidates so the user can opt-in via the panel.
      const polyRows = (res.data?.components || [])
        .filter(c => (c.category || "").toUpperCase() === "POLY");
      if (polyRows.length === 1) {
        const polyId = polyRows[0].rm_id;
        if (polyId !== targetPolyRmId) {
          setTargetPolyRmId(polyId);
        }
        // Fetch regrinds whose parent_grades include this POLY.
        try {
          const r = await axios.get(`${API}/raw-materials`,
            { params: { category: "REGRIND", limit: 5000 }, headers: getHeaders() });
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          // Defensive client-side filter — backend `category` query param
          // is ignored as of Feb 2026 (testing-agent finding), so without
          // this filter we'd see ALL raw materials in the regrind picker.
          const regrindOnly = items.filter(it => (it.category || "").toUpperCase() === "REGRIND");
          setRegrindOptions(regrindOnly.filter(it =>
            (it.parent_grades || []).includes(polyId)
          ));
        } catch { setRegrindOptions([]); }
      } else if (polyRows.length === 0) {
        setRegrindOptions([]);
        setTargetPolyRmId(null);
      }
      // Multi-POLY case is handled inside the panel UI (user picks).
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview production");
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmProduction = async () => {
    if (!previewData || !previewData.can_produce) return;
    
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/rm-production/confirm`,
        {
          branch: selectedBranch,
          rm_id: previewData.rm_id,
          quantity_produced: previewData.quantity_to_produce,
          production_date: productionDate,
          notes: notes,
          // REGRIND_RECYCLE_PRD §FR-C4 — mirror the preview inputs so
          // backend can re-apply the split during the consume loop.
          regrind_feeds: regrindFeeds.length
            ? regrindFeeds.map(f => ({ rm_id: f.rm_id, pct: parseFloat(f.pct) || 0 }))
                          .filter(f => f.rm_id && f.pct > 0)
            : [],
          target_poly_rm_id: targetPolyRmId || null,
          // Jun 2026 P0 — lock the operator's MB dropdown selections
          // so the confirm path deducts from exactly those MBs.
          overrides: substitutionOverrides,
        },
        { headers: getHeaders() }
      );
      
      toast.success(res.data.message || "Production confirmed successfully");
      setShowPreviewDialog(false);
      setPreviewData(null);
      setSelectedRM(null);
      setQuantity("");
      setNotes("");
      // Reset regrind state after a successful confirm so the next
      // production preview starts clean.
      setRegrindFeeds([]);
      setRegrindOptions([]);
      setTargetPolyRmId(null);
      setSubstitutionOverrides({});
      
      // Refresh data
      fetchManufacturableRMs();
      if (activeTab === "log") fetchProductionLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm production");
    } finally {
      setConfirming(false);
    }
  };

  const toggleRowExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const exportProductionLog = () => {
    if (productionLog.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = productionLog.map(log => ({
      "Production Code": log.production_code,
      "Date": log.production_date,
      "RM ID": log.rm_id,
      "RM Name": log.rm_name,
      "Category": log.category,
      "BOM Level": `L${log.bom_level}`,
      "Quantity Produced": log.quantity_produced,
      "UOM": log.uom,
      "Stock Before": log.stock_before,
      "Stock After": log.stock_after,
      "Produced By": log.produced_by_name,
      "Notes": log.notes || ""
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 10 },
      { wch: 10 }, { wch: 15 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
      { wch: 20 }, { wch: 30 }
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Production Log");
    
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf]), `RM_Production_Log_${selectedBranch}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Production log exported");
  };

  const exportConsumptionReport = () => {
    if (!consumptionReport?.consumption?.length) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = consumptionReport.consumption.map(item => ({
      "RM ID": item.rm_id,
      "RM Name": item.name,
      "Total Consumed": item.total_consumed,
      "UOM": item.uom,
      "Used In (Count)": item.used_in_count
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consumption Report");
    
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf]), `RM_Consumption_Report_${selectedBranch}_${summaryPeriod.start}_to_${summaryPeriod.end}.xlsx`);
    toast.success("Consumption report exported");
  };

  return (
    <div className="p-6 space-y-6" data-testid="rm-production-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="w-6 h-6 text-orange-500" />
            RM Production Inward
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manufacture RMs from components using defined BOMs
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="w-[220px]">
            <Label className="text-xs text-gray-500">Branch</Label>
            <Select
              value={selectedBranch || "select"}
              onValueChange={(v) => {
                // Hard guard: never accept a branch the user can't target.
                // Shadcn's `SelectItem disabled` already blocks click, but
                // some keyboard paths still fire onValueChange.
                if (!isAllBranchAccess && !allowedBranches.includes(v)) return;
                setSelectedBranch(v);
              }}
            >
              <SelectTrigger data-testid="branch-select">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((name) => {
                  const allowed = isAllBranchAccess || allowedBranches.includes(name);
                  return (
                    <SelectItem
                      key={name}
                      value={name}
                      disabled={!allowed}
                      data-testid={`branch-option-${name.replace(/\s+/g, "-")}`}
                      className={!allowed ? "text-zinc-400 bg-zinc-50/60" : ""}
                    >
                      <span className="flex items-center gap-2">
                        {!allowed ? (
                          <Lock className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        ) : (
                          <span className="w-3.5 h-3.5 shrink-0" />
                        )}
                        <span>{name}</span>
                        {!allowed && (
                          <span className="text-[10px] text-zinc-400 ml-1">(no access)</span>
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {!isAllBranchAccess && allowedBranches.length === 1 && (
              <div className="text-[10px] text-gray-400 mt-0.5">Locked to your assigned branch</div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-[500px] grid-cols-3">
          <TabsTrigger value="produce" data-testid="produce-tab">
            <Plus className="w-4 h-4 mr-2" />
            Produce RM
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="log-tab">
            <FileText className="w-4 h-4 mr-2" />
            Production Log
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="reports-tab">
            <Factory className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Produce RM Tab */}
        <TabsContent value="produce" className="space-y-6">
          {!selectedBranch ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Factory className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Please select a branch to start production</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: RM Selection */}
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Select RM to Produce</CardTitle>
                    <CardDescription>
                      Choose a category and select an RM with an active BOM
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {unconfiguredCategories.length > 0 && (
                      <div
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                        data-testid="unconfigured-categories-banner"
                      >
                        <strong>{unconfiguredCategories.length}</strong> categor
                        {unconfiguredCategories.length === 1 ? "y is" : "ies are"} missing a
                        <code className="mx-1 rounded bg-amber-100 px-1">default_source_type</code>:
                        <span className="font-mono">{unconfiguredCategories.join(", ")}</span>.
                        RMs in {unconfiguredCategories.length === 1 ? "it" : "them"} won't appear here until
                        the field is set under <em>Master Data → Categories</em>.
                      </div>
                    )}
                    <div className="flex gap-3 items-end">
                      <div className="w-72">
                        <Label>Category</Label>
                        <Select value={selectedCategory || "all"} onValueChange={(v) => {
                          setSelectedCategory(v === "all" ? "" : v);
                          setSelectedRM(null);
                        }}>
                          <SelectTrigger data-testid="category-select">
                            <SelectValue placeholder="All Categories" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label>Search RM</Label>
                        <Input
                          type="text"
                          placeholder="Search RM ID or name..."
                          value={rmSearch}
                          onChange={(e) => setRmSearch(e.target.value)}
                          data-testid="rm-search-input"
                        />
                      </div>
                      <Button variant="outline" onClick={fetchManufacturableRMs} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                    
                    {/* RM List */}
                    <div className="border rounded-lg max-h-[400px] overflow-auto">
                      {loading ? (
                        <div className="p-8 text-center text-gray-500">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                          Loading manufacturable RMs...
                        </div>
                      ) : manufacturableRMs.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No manufacturable RMs found</p>
                          <p className="text-xs mt-1">Ensure BOMs are defined for manufactured RMs</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]"></TableHead>
                              <TableHead>RM ID</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead>Level</TableHead>
                              <TableHead>Current Stock</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {manufacturableRMs
                              .filter(rm => {
                                if (!rmSearch.trim()) return true;
                                const q = rmSearch.trim().toLowerCase();
                                return (rm.rm_id || "").toLowerCase().includes(q)
                                  || (rm.rm_name || "").toLowerCase().includes(q);
                              })
                              .map(rm => (
                              <TableRow 
                                key={rm.rm_id}
                                className={`cursor-pointer ${selectedRM?.rm_id === rm.rm_id ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                                onClick={() => setSelectedRM(rm)}
                                data-testid={`rm-row-${rm.rm_id}`}
                              >
                                <TableCell>
                                  <input 
                                    type="radio" 
                                    checked={selectedRM?.rm_id === rm.rm_id}
                                    onChange={() => setSelectedRM(rm)}
                                    className="w-4 h-4 text-orange-500"
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-sm">{rm.rm_id}</TableCell>
                                <TableCell className="max-w-[200px] truncate">{rm.rm_name}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{rm.category}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-blue-100 text-blue-700">L{rm.bom_level}</Badge>
                                </TableCell>
                                <TableCell>
                                  <span className={rm.current_stock > 0 ? 'text-green-600' : 'text-gray-400'}>
                                    {rm.current_stock} {rm.uom}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Right: Production Input */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Production Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Active branch banner — always visible. The produced
                        inventory will land in this branch. Branch Ops users
                        cannot change this; Master Admin can switch via the
                        dropdown at the top of the page. */}
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50"
                      data-testid="rm-production-active-branch"
                    >
                      <Package className="w-4 h-4 text-emerald-700" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-700">Producing into</div>
                        <div className="text-sm font-semibold text-emerald-900 truncate">
                          {selectedBranch || "No branch selected"}
                        </div>
                      </div>
                    </div>
                    {selectedRM ? (
                      <>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <p className="font-medium text-orange-800">{selectedRM.rm_id}</p>
                          <p className="text-sm text-orange-600">{selectedRM.rm_name}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{selectedRM.category}</Badge>
                            <Badge className="bg-blue-100 text-blue-700">L{selectedRM.bom_level}</Badge>
                          </div>
                        </div>
                        
                        <div>
                          <Label>Quantity to Produce *</Label>
                          <Input
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            placeholder={`Enter quantity in ${selectedRM.uom}`}
                            min="0.01"
                            step="0.01"
                            className="no-spinner"
                            data-testid="quantity-input"
                          />
                        </div>
                        
                        <div>
                          <Label>Production Date</Label>
                          <Input
                            type="date"
                            value={productionDate}
                            onChange={(e) => setProductionDate(e.target.value)}
                            data-testid="production-date-input"
                          />
                        </div>
                        
                        <div>
                          <Label>Notes (Optional)</Label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add production notes..."
                            rows={3}
                            data-testid="notes-input"
                          />
                        </div>
                        
                        <Button 
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          onClick={handlePreview}
                          disabled={previewing || !quantity || parseFloat(quantity) <= 0}
                          data-testid="preview-production-btn"
                        >
                          {previewing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4 mr-2" />
                          )}
                          Preview Production
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Select an RM to produce</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Production Log Tab */}
        <TabsContent value="log" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Production Log</CardTitle>
                  <CardDescription>History of all RM production entries</CardDescription>
                </div>
                <Button variant="outline" onClick={exportProductionLog}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4">
                <div className="w-[150px]">
                  <Label className="text-xs">Category</Label>
                  <Select value={logFilters.category || "all"} onValueChange={(v) => {
                    setLogFilters(f => ({ ...f, category: v === "all" ? "" : v }));
                    setLogPage(1);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[150px]">
                  <Label className="text-xs">RM ID</Label>
                  <Input
                    value={logFilters.rm_id}
                    onChange={(e) => {
                      setLogFilters(f => ({ ...f, rm_id: e.target.value }));
                      setLogPage(1);
                    }}
                    placeholder="Search RM ID"
                  />
                </div>
                <div className="w-[150px]">
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={logFilters.start_date}
                    onChange={(e) => {
                      setLogFilters(f => ({ ...f, start_date: e.target.value }));
                      setLogPage(1);
                    }}
                  />
                </div>
                <div className="w-[150px]">
                  <Label className="text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={logFilters.end_date}
                    onChange={(e) => {
                      setLogFilters(f => ({ ...f, end_date: e.target.value }));
                      setLogPage(1);
                    }}
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => {
                    setLogFilters({ category: "", rm_id: "", start_date: "", end_date: "" });
                    setLogPage(1);
                  }}>
                    Clear
                  </Button>
                </div>
              </div>
              
              {/* Log Table */}
              <div className="border rounded-lg">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </div>
                ) : productionLog.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No production logs found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>RM ID</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Qty Produced</TableHead>
                        <TableHead>Stock Before</TableHead>
                        <TableHead>Stock After</TableHead>
                        <TableHead>By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productionLog.map(log => (
                        <>
                          <TableRow key={log.id} className="cursor-pointer hover:bg-gray-50" onClick={() => toggleRowExpand(log.id)}>
                            <TableCell>
                              {expandedRows.has(log.id) ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{log.production_code}</TableCell>
                            <TableCell>{log.production_date}</TableCell>
                            <TableCell className="font-mono">{log.rm_id}</TableCell>
                            <TableCell><Badge variant="outline">{log.category}</Badge></TableCell>
                            <TableCell className="font-medium text-green-600">
                              +{log.quantity_produced} {log.uom}
                            </TableCell>
                            <TableCell>{log.stock_before}</TableCell>
                            <TableCell className="font-medium">{log.stock_after}</TableCell>
                            <TableCell className="text-sm text-gray-500">{log.produced_by_name}</TableCell>
                          </TableRow>
                          {expandedRows.has(log.id) && (
                            <TableRow key={`${log.id}-expanded`}>
                              <TableCell colSpan={9} className="bg-gray-50 p-4">
                                <div className="space-y-2">
                                  <p className="text-sm font-medium">Components Consumed:</p>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {log.components_consumed?.map((comp, idx) => (
                                      <div key={idx} className="p-2 bg-white border rounded text-sm">
                                        <p className="font-mono text-xs text-gray-500">{comp.rm_id}</p>
                                        <p className="font-medium text-red-600">
                                          -{comp.quantity_consumed} {comp.uom}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                          {comp.stock_before} → {comp.stock_after}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                  {log.notes && (
                                    <p className="text-sm text-gray-500 mt-2">
                                      <span className="font-medium">Notes:</span> {log.notes}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              
              {/* Pagination */}
              {logTotalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing page {logPage} of {logTotalPages} ({logTotal} total entries)
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={logPage <= 1}
                      onClick={() => setLogPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={logPage >= logTotalPages}
                      onClick={() => setLogPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Period Selection */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Report Period:</span>
                </div>
                <Input
                  type="date"
                  value={summaryPeriod.start}
                  onChange={(e) => setSummaryPeriod(p => ({ ...p, start: e.target.value }))}
                  className="w-[160px]"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="date"
                  value={summaryPeriod.end}
                  onChange={(e) => setSummaryPeriod(p => ({ ...p, end: e.target.value }))}
                  className="w-[160px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Produced</p>
                      <p className="text-2xl font-bold text-orange-600">{summary.totals?.total_produced || 0}</p>
                    </div>
                    <Factory className="w-8 h-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Production Entries</p>
                      <p className="text-2xl font-bold">{summary.totals?.total_entries || 0}</p>
                    </div>
                    <FileText className="w-8 h-8 text-gray-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Categories Active</p>
                      <p className="text-2xl font-bold text-blue-600">{summary.categories?.length || 0}</p>
                    </div>
                    <Package className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Category Summary */}
          {summary?.categories?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Production by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Total Produced</TableHead>
                      <TableHead>Entries</TableHead>
                      <TableHead>Unique Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.categories.map(cat => (
                      <TableRow key={cat.category}>
                        <TableCell>
                          <Badge variant="outline">{cat.category}</Badge>
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          {cat.total_produced}
                        </TableCell>
                        <TableCell>{cat.production_count}</TableCell>
                        <TableCell>{cat.unique_items}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Consumption Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Component Consumption Report</CardTitle>
                  <CardDescription>L1 materials consumed during production</CardDescription>
                </div>
                <Button variant="outline" onClick={exportConsumptionReport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {consumptionReport?.consumption?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>RM ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Total Consumed</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead>Used In</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consumptionReport.consumption.map(item => (
                      <TableRow key={item.rm_id}>
                        <TableCell className="font-mono">{item.rm_id}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="font-medium text-red-600">
                          {item.total_consumed}
                        </TableCell>
                        <TableCell>{item.uom}</TableCell>
                        <TableCell>{item.used_in_count} productions</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No consumption data for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Production Preview
            </DialogTitle>
            <DialogDescription>
              Review component requirements and stock availability before confirming
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              {/* Active branch banner — same one shown in the sidebar. Makes
                  it explicit *where* the produced inventory will land before
                  the user clicks Confirm. */}
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50"
                data-testid="preview-active-branch"
              >
                <Package className="w-4 h-4 text-emerald-700" />
                <div className="text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 mr-2">Producing into</span>
                  <span className="font-semibold text-emerald-900">{selectedBranch}</span>
                </div>
              </div>
              {/* Output Info */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm text-gray-500">{previewData.rm_id}</p>
                    <p className="font-medium text-lg">{previewData.rm_name}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline">{previewData.category}</Badge>
                      <Badge className="bg-blue-100 text-blue-700">L{previewData.bom_level}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Producing</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {previewData.quantity_to_produce} {previewData.output_uom}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Status Banner */}
              {previewData.can_produce ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-medium">All components available - Ready to produce</span>
                </div>
              ) : (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-700 font-medium">
                    Insufficient stock for: {previewData.blocking_components?.join(", ")}
                  </span>
                </div>
              )}
              
              {/* Components Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Component</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.components?.map(comp => {
                      // Jun 2026 P0 — colourant-aware substitution row.
                      // For MB/PWD components on a colourant, the backend
                      // returns `candidates[]` (same-colourant + same-pantone
                      // MBs with their branch stock). Render the operator
                      // override dropdown here; derive status from the
                      // currently-selected candidate's stock so the row
                      // updates immediately on override change without a
                      // re-preview round-trip.
                      const hasCandidates = Array.isArray(comp.candidates) && comp.candidates.length > 0;
                      const bomKey = comp.bom_rm_id || comp.rm_id;
                      const pickedRmId = hasCandidates
                        ? (substitutionOverrides[bomKey] || comp.resolved_rm_id || comp.rm_id)
                        : comp.rm_id;
                      const pickedCand = hasCandidates
                        ? comp.candidates.find(c => c.rm_id === pickedRmId)
                        : null;
                      const rowAvailable = pickedCand
                        ? pickedCand.branch_stock
                        : comp.available_stock;
                      const rowShortage = Math.max(0, Number(((comp.required_qty || 0) - rowAvailable).toFixed(4)));
                      const rowSufficient = rowAvailable >= (comp.required_qty || 0);
                      // Aug 2026 — display resolution moved to a
                      // shared pure module so the "candidate's own
                      // description" + no-duplicate + fallback rules
                      // are exercised by a screen-level test without
                      // reproducing the logic inline. See
                      // `lib/mbCandidateDisplay.js`.
                      const { primaryId, secondaryLine } = resolveComponentDisplay(
                        comp, pickedCand, pickedRmId,
                      );
                      return (
                        <TableRow key={bomKey} data-testid={`component-row-${bomKey}`}>
                          <TableCell>
                            <p
                              className="font-mono text-xs text-gray-500"
                              data-testid={`component-rm-id-${bomKey}`}
                            >
                              {primaryId}
                            </p>
                            {secondaryLine && (
                              <p
                                className="text-sm"
                                data-testid={`component-desc-${bomKey}`}
                              >
                                {secondaryLine}
                              </p>
                            )}
                            {hasCandidates && (
                              <p className="text-[10px] text-gray-400">
                                {comp.candidates.length} MB{comp.candidates.length === 1 ? "" : "s"} available
                              </p>
                            )}
                            {hasCandidates && (
                              <div className="mt-1" data-testid={`mb-override-select-${bomKey}`}>
                                <Select
                                  value={pickedRmId}
                                  onValueChange={(v) => setSubstitutionOverrides(prev => ({ ...prev, [bomKey]: v }))}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {comp.candidates.map(cand => (
                                      <SelectItem
                                        key={cand.rm_id}
                                        value={cand.rm_id}
                                        data-testid={`mb-override-opt-${bomKey}-${cand.rm_id}`}
                                      >
                                        {cand.rm_id} · {Number(cand.branch_stock).toFixed(2)} {comp.uom} in stock
                                        {cand.is_preferred ? " · PREFERRED" : ""}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {comp.required_qty} {comp.uom}
                          </TableCell>
                          <TableCell>
                            {rowAvailable} {comp.uom}
                          </TableCell>
                          <TableCell>
                            {rowSufficient ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                OK
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Short {rowShortage}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* REGRIND_RECYCLE_PRD §FR-C4 — opt-in regrind blend panel.
                  Empty by default → no-regrind path is unchanged. */}
              {(() => {
                const polyRows = (previewData.components || [])
                  .filter(c => (c.category || "").toUpperCase() === "POLY");
                const showMultiPolyPicker = polyRows.length > 1;
                const hasPoly = polyRows.length > 0;
                if (!hasPoly) return null;
                return (
                  <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-lg p-3 space-y-3"
                       data-testid="regrind-panel">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">Regrind used (optional)</p>
                        <p className="text-[11px] text-emerald-800">
                          Each % feed replaces that share of fresh polymer. Sum must be ≤ 100.
                        </p>
                      </div>
                      <Button variant="outline" size="sm"
                              disabled={regrindOptions.length === 0}
                              onClick={() => setRegrindFeeds(prev => [
                                ...prev, { rm_id: "", pct: "" }
                              ])}
                              data-testid="regrind-add-feed-btn">
                        + Add Regrind Feed
                      </Button>
                      <Button variant="outline" size="sm"
                              disabled={previewing || regrindFeeds.length === 0}
                              className="ml-2 border-2 border-emerald-600 text-emerald-700 font-semibold hover:bg-emerald-100"
                              onClick={async () => {
                                // REGRIND_RECYCLE_PRD Feb 2026 — recompute
                                // the preview in place using current feeds.
                                // Replaces the old "close dialog → re-click
                                // Preview" two-step that users were missing.
                                try {
                                  setPreviewing(true);
                                  const res = await axios.post(
                                    `${API}/rm-production/preview`,
                                    {
                                      branch: selectedBranch,
                                      rm_id: previewData.rm_id,
                                      quantity_to_produce: previewData.quantity_to_produce,
                                      regrind_feeds: regrindFeeds
                                        .map(f => ({ rm_id: f.rm_id, pct: parseFloat(f.pct) || 0 }))
                                        .filter(f => f.rm_id && f.pct > 0),
                                      target_poly_rm_id: targetPolyRmId || null,
                                    },
                                    { headers: getHeaders() }
                                  );
                                  setPreviewData(res.data);
                                  toast.success("Preview recomputed with regrind split");
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Failed to recompute");
                                } finally {
                                  setPreviewing(false);
                                }
                              }}
                              data-testid="regrind-recompute-btn">
                        {previewing ? "Recomputing…" : "↻ Recompute"}
                      </Button>
                    </div>

                    {showMultiPolyPicker && (
                      <div className="border bg-white rounded p-2">
                        <Label className="text-xs">Target Polymer (BOM has multiple POLY components)</Label>
                        <select
                          className="block w-full mt-1 border rounded px-2 py-1 text-sm"
                          value={targetPolyRmId || ""}
                          onChange={(e) => setTargetPolyRmId(e.target.value)}
                          data-testid="regrind-target-poly-select"
                        >
                          <option value="">Pick the polymer the regrind substitutes for…</option>
                          {polyRows.map(p => (
                            <option key={p.rm_id} value={p.rm_id}>{p.rm_id} — {p.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {regrindFeeds.length === 0 ? (
                      <p className="text-[11px] text-emerald-700">
                        {regrindOptions.length === 0
                          ? "No regrind items match this polymer (create one with this Polymer ID in RM Repository first)."
                          : "No regrind feed added. Click + Add Regrind Feed to blend recycled material into this batch."}
                      </p>
                    ) : regrindFeeds.map((feed, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white rounded border p-2"
                           data-testid={`regrind-feed-row-${idx}`}>
                        <div className="col-span-7">
                          <Label className="text-xs">Regrind Item</Label>
                          <select
                            className="block w-full mt-1 border rounded px-2 py-1 text-sm font-mono"
                            value={feed.rm_id}
                            onChange={(e) => setRegrindFeeds(prev => prev.map((f, i) =>
                              i === idx ? { ...f, rm_id: e.target.value } : f))}
                            data-testid={`regrind-feed-rm-${idx}`}
                          >
                            <option value="">Pick a regrind…</option>
                            {regrindOptions.map(opt => (
                              <option key={opt.rm_id} value={opt.rm_id}>
                                {opt.rm_id} — {opt.description || opt?.category_data?.colour || ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">% of polymer</Label>
                          <Input type="number" min="0.01" max="100" step="0.1"
                                 value={feed.pct}
                                 onChange={(e) => setRegrindFeeds(prev => prev.map((f, i) =>
                                   i === idx ? { ...f, pct: e.target.value } : f))}
                                 placeholder="e.g. 20"
                                 className="no-spinner"
                                 data-testid={`regrind-feed-pct-${idx}`} />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <Button variant="outline" size="sm"
                                  onClick={() => setRegrindFeeds(prev => prev.filter((_, i) => i !== idx))}
                                  data-testid={`regrind-feed-remove-${idx}`}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}

                    {regrindFeeds.length > 0 && (
                      <div className="text-[11px] text-emerald-900">
                        Total feed:{" "}
                        <span className="font-semibold">
                          {regrindFeeds.reduce((a, f) => a + (parseFloat(f.pct) || 0), 0).toFixed(1)}%
                        </span>
                        {" "}— click <strong>↻ Recompute</strong> to apply the split to the BOM table above.
                      </div>
                    )}

                    {(previewData.warnings || []).length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2"
                           data-testid="regrind-warnings">
                        <p className="text-xs font-semibold text-amber-900 mb-1">Warnings (non-blocking)</p>
                        {(previewData.warnings || []).map((w, i) => (
                          <p key={i} className="text-[11px] text-amber-800">• {w}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Production Date & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Production Date</Label>
                  <Input type="date" value={productionDate} onChange={(e) => setProductionDate(e.target.value)} />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Cancel
            </Button>
            <PermButton
              entity="ProductionBatch" action="CREATE"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleConfirmProduction}
              disabled={confirming || !previewData?.can_produce}
              data-testid="confirm-production-btn"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Production
            </PermButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RMProduction;
